import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { ConnectShopifyIntegrationCta } from "@v2/components/ConnectShopifyCallout";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import ProductHostingTypeSelector from "@v2/features/onboarding/ProductHostingTypeSelector";
import { ProductTypeSelector } from "@v2/features/products/components/ProductTypeSelector";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { stripeConnectRouter } from "@v2/trpc/routes/stripeConnect";
import { routes } from "@v2/utils/router";
import { ArrowRight } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";
import { config, useSpring } from "react-spring";

type SetupOption = "new" | "import" | "ai" | "themeBuilder" | "import-later";

export function ProductsSetup() {
  const [selectedHostingType, setSelectedHostingType] =
    React.useState<SetupOption | null>("new");
  const [showProductTypeSelection, setShowProductTypeSelection] =
    React.useState(false);
  const currentProjectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { data: onboardingStatus } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      currentProjectId ? { projectId: currentProjectId } : skipToken,
    );

  const isStripeConnected = onboardingStatus?.isConnected ?? false;

  const slideUpAnimation = useSpring({
    config: config.gentle,
    transform: "translateY(0px)",
    opacity: 1,
  });

  const handleSelectProductType = (productType: string) => {
    navigate(
      generatePath(routes.shops.products.new, {
        projectId: currentProjectId,
      }),
      {
        state: { selectedProductType: productType },
      },
    );
  };

  const handleAddFirstProduct = () => {
    setShowProductTypeSelection(true);
  };

  const handleBack = () => {
    setShowProductTypeSelection(false);
  };

  if (isStripeConnected) {
    return (
      <div
        className={twMerge(
          "flex flex-col w-full max-w-4xl min-w-0 h-full items-center",
          isMobile
            ? "px-4 justify-start gap-8 py-6"
            : "justify-start gap-14 pt-32",
        )}
      >
        <ProductTypeSelector onSelectType={handleSelectProductType} />
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "flex flex-col w-full max-w-4xl min-w-0 h-full items-center",
        isMobile
          ? "px-4 justify-start gap-8 py-6"
          : "justify-start gap-14 pt-32",
        showProductTypeSelection && !isMobile && "pt-24",
      )}
    >
      {!showProductTypeSelection ? (
        <>
          <div className="flex flex-col gap-4 items-center justify-center text-center">
            <div className="text-3xl font-semibold leading-7 text-default">
              How do you want to sell?
            </div>
            <div className="text-muted header-h4 font-normal">
              Choose your product and checkout setup
            </div>
          </div>
          <ProductHostingTypeSelector
            selectedHostingType={selectedHostingType}
            setSelectedHostingType={setSelectedHostingType}
            slideUpAnimation={slideUpAnimation}
            hideSetupShopifyLaterOption
            className="md:flex-col"
            showDefaultHeader={false}
          />
          <div className="flex w-full justify-end">
            {selectedHostingType === "new" ? (
              <Button
                variant="primary"
                size="base"
                endEnhancer={<ArrowRight />}
                onClick={handleAddFirstProduct}
              >
                Add first product
              </Button>
            ) : (
              <ConnectShopifyIntegrationCta
                cookieValue={{
                  type: "productPicker",
                  projectId: currentProjectId!,
                  workspaceId: workspaceId!,
                }}
                ctaText="Connect to Shopify"
                size="base"
                showEndIcon={true}
                className="w-fit"
              />
            )}
          </div>
        </>
      ) : (
        <ProductTypeSelector
          onSelectType={handleSelectProductType}
          onBack={handleBack}
        />
      )}
    </div>
  );
}
